import { useAtomValue, useSetAtom } from 'jotai'
import React from 'react'
import { z } from 'zod'

import { brandGroupIdAtom } from '~/atoms'
import { dataDisplayTypes, segmentDisplayTypes, setValueIfExist } from '~/utils'

import {
  brandIdsAtom,
  commentByViewKeyAtom,
  dataDisplayTypeAtom,
  importanceQuestionIdAtom,
  liftSaQuestionIdAtom,
  npiDriverQuestionIdAtom,
  pendingScrollHashAtom,
  savedCommentByViewKeyAtom,
  segmentDisplayTypeAtom,
  selectedRouteAtom,
} from '../../atoms'
import { buildCommentKey } from '../../utils'

const schema = z.object({
  version: z.literal(1),
  brandGroupId: z.number().optional(),
  brandIds: z.array(z.number()),
  dataDisplayType: z.enum(dataDisplayTypes).optional(),
  npiDriverQuestionId: z.string().nullable(),
  importanceQuestionId: z.string().nullable(),
  liftSaQuestionId: z.string().nullable().optional(),
  segmentDisplayType: z.enum(segmentDisplayTypes).optional(),
  fromSegmentNumber: z.number(),
  toSegmentNumber: z.number(),
  comment: z.string().optional(),
  hash: z.union([
    z.literal('npi-driver'),
    z.string().regex(/^segment-panel-\d+$/),
  ]),
})

export const useRestoreParams = () => {
  const setBrandGroupId = useSetAtom(brandGroupIdAtom)
  const setBrandIds = useSetAtom(brandIdsAtom)
  const setDataDisplayType = useSetAtom(dataDisplayTypeAtom)
  const setNpiDriverQuestionId = useSetAtom(npiDriverQuestionIdAtom)
  const setImportanceQuestionId = useSetAtom(importanceQuestionIdAtom)
  const setLiftSaQuestionId = useSetAtom(liftSaQuestionIdAtom)
  const currentSegmentDisplayType = useAtomValue(segmentDisplayTypeAtom)
  const setSegmentDisplayType = useSetAtom(segmentDisplayTypeAtom)
  const setSelectedRoute = useSetAtom(selectedRouteAtom)
  const setPendingScrollHash = useSetAtom(pendingScrollHashAtom)
  const setCommentByViewKey = useSetAtom(commentByViewKeyAtom)
  const setSavedCommentByViewKey = useSetAtom(savedCommentByViewKeyAtom)

  return React.useCallback(
    (state: unknown): true | null => {
      const parsed = schema.safeParse(state)
      if (!parsed.success) {
        return null
      }

      const params = parsed.data
      setValueIfExist(params.brandGroupId, setBrandGroupId)
      setValueIfExist(params.brandIds, setBrandIds)
      setValueIfExist(params.dataDisplayType, setDataDisplayType)
      setValueIfExist(params.npiDriverQuestionId, setNpiDriverQuestionId)
      setValueIfExist(params.importanceQuestionId, setImportanceQuestionId)
      setValueIfExist(params.liftSaQuestionId, setLiftSaQuestionId)
      setValueIfExist(params.segmentDisplayType, setSegmentDisplayType)

      if (params.comment != null) {
        const commentKey = buildCommentKey({
          segmentDisplayType:
            params.segmentDisplayType ?? currentSegmentDisplayType,
          fromSegmentNumber: params.fromSegmentNumber,
          toSegmentNumber: params.toSegmentNumber,
        })
        const comment = params.comment
        setCommentByViewKey((prev) => ({ ...prev, [commentKey]: comment }))
        setSavedCommentByViewKey((prev) => ({ ...prev, [commentKey]: comment }))
      }

      if (params.fromSegmentNumber != null && params.toSegmentNumber != null) {
        setSelectedRoute({
          fromSegmentNumber: params.fromSegmentNumber,
          toSegmentNumber: params.toSegmentNumber,
        })
      }

      if (params.hash != null) {
        setPendingScrollHash(params.hash)
      }

      return true
    },
    [
      currentSegmentDisplayType,
      setBrandGroupId,
      setBrandIds,
      setCommentByViewKey,
      setDataDisplayType,
      setImportanceQuestionId,
      setLiftSaQuestionId,
      setNpiDriverQuestionId,
      setPendingScrollHash,
      setSavedCommentByViewKey,
      setSegmentDisplayType,
      setSelectedRoute,
    ],
  )
}
